import { IRL_COUNTIES, irlPhone, irlPostcode } from "@homeinstead/locale";
import { z } from "zod";
import { email } from "~www/types/forms/schemas/email";
import { firstName } from "~www/types/forms/schemas/first-name";
import { lastName } from "~www/types/forms/schemas/last-name";

type CountyValue = (typeof IRL_COUNTIES)[number];

const countyEnum = z.enum(IRL_COUNTIES);

type FranchiseInquiryResponse = {
  isError: boolean;
  errors: { code: string; detail: string; reason: string; source: string }[];
  message: string;
  id?: string;
};

const franchiseInquiryFormSchema = z.object({
  county: z
    .string()
    .min(1, { message: "Please select a county" })
    .refine(
      (value): value is CountyValue =>
        IRL_COUNTIES.includes(value as CountyValue),
      { message: "Please select a valid county" },
    ),
  email,
  firstName,
  lastName,
  marketingOptIn: z.boolean(),
  message: z
    .string()
    .max(2000, { message: "Message must be 2000 characters or less" }),
  phone: irlPhone,
  postcode: irlPostcode,
});

type FranchiseInquiryFormInput = {
  county: string;
  email: string;
  firstName: string;
  lastName: string;
  marketingOptIn: boolean;
  message: string;
  phone: string;
  postcode: string;
};

type FranchiseInquiryFormData = z.infer<typeof franchiseInquiryFormSchema>;

function getFranchiseInquiryDefaultFormValues(): FranchiseInquiryFormInput {
  return {
    county: "",
    email: "",
    firstName: "",
    lastName: "",
    marketingOptIn: false,
    message: "",
    phone: "",
    postcode: "",
  };
}

function parseFranchiseInquiryFormData(
  formData: FormData,
): Record<string, unknown> {
  return {
    county: formData.get("county"),
    email: formData.get("email"),
    firstName: formData.get("firstName"),
    lastName: formData.get("lastName"),
    marketingOptIn: formData.get("marketingOptIn") === "on",
    message: formData.get("message"),
    phone: formData.get("phone"),
    postcode: formData.get("postcode"),
  };
}

function parseFranchiseInquiryBlockReference(formData: FormData): {
  blockKey: string;
  documentId: string;
} | null {
  const blockKey = formData.get("inquiryBlockKey");
  const documentId = formData.get("inquiryDocumentId");

  if (typeof blockKey !== "string" || blockKey.trim() === "") {
    return null;
  }

  if (typeof documentId !== "string" || documentId.trim() === "") {
    return null;
  }

  return {
    blockKey: blockKey.trim(),
    documentId: documentId.trim(),
  };
}

export type {
  CountyValue,
  FranchiseInquiryFormData,
  FranchiseInquiryFormInput,
  FranchiseInquiryResponse,
};

export {
  countyEnum,
  franchiseInquiryFormSchema,
  getFranchiseInquiryDefaultFormValues,
  parseFranchiseInquiryBlockReference,
  parseFranchiseInquiryFormData,
};
